// app/javascript/src/pages/Admin/PaymentTerms/PaymentTermForm/PaymentTermForm.tsx
import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import { TermsEditor } from "./TermsEditor";
import { validateTerms } from "./validation";
import "./style.css";
import { PRICING_MODEL_OPTIONS, ROUNDING_OPTIONS } from "../types";
import type { PaymentTermFormValues, TermFormRow, TermType } from "../types";

export interface PaymentTermFormProps {
  id: string;
  initialValues: PaymentTermFormValues;
  isSubmitting: boolean;
  onSubmit: (values: PaymentTermFormValues) => void;
  onInvalid: (message: string) => void;
}

// Small left-justified toggle field (label above a GenericToggle).
const ToggleField = ({
  label,
  value,
  onToggle,
  resetKey,
  colClassName = "col-sm-4",
  helpText,
}: {
  label: string;
  value: boolean;
  onToggle: (next: boolean) => void;
  resetKey: string;
  colClassName?: string;
  helpText?: string;
}): JSX.Element => (
  <div className={colClassName}>
    <Form.Label>{label}</Form.Label>
    <div className="paymentTermToggleCell">
      <GenericToggle
        key={resetKey}
        initialValueOfIsOn={value}
        onToggle={onToggle}
        onText="Yes"
        offText="No"
        hasText
      />
    </div>
    {helpText && <small className="small text-muted d-block">{helpText}</small>}
  </div>
);

export const PaymentTermForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
  onInvalid,
}: PaymentTermFormProps): JSX.Element => {
  const [values, setValues] = useState<PaymentTermFormValues>(initialValues);

  const set = <K extends keyof PaymentTermFormValues>(
    key: K,
    v: PaymentTermFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    const error = validateTerms(values.termType, values.terms);
    if (error) {
      onInvalid(error);
      return;
    }
    onSubmit(values);
  };

  const selectedPricing = PRICING_MODEL_OPTIONS.find(
    (o) => o.value === values.pricingModel
  );
  const selectedRounding = ROUNDING_OPTIONS.find(
    (o) => o.value === values.rounding
  );

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      {/* Row 1: Name */}
      <div className="row">
        <div className="col-sm-12">
          <Form.Label htmlFor="payment-term-name">Name</Form.Label>
          <TextField
            label={null}
            id="payment-term-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
      </div>

      {/* Row 2: Pricing Model, Currency Rounding, Default */}
      <div className="row" style={{ marginTop: "1em" }}>
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Pricing Model"
            value={selectedPricing}
            onChange={(opt: any) => set("pricingModel", opt?.value ?? "")}
            options={PRICING_MODEL_OPTIONS}
            placeholder="Select…"
            required
          />
        </div>
        <div className="col-sm-4">
          <RequiredReactSelect
            label="Currency Rounding"
            value={selectedRounding}
            onChange={(opt: any) => set("rounding", opt?.value ?? "")}
            options={ROUNDING_OPTIONS}
            placeholder="Select…"
            required
          />
        </div>
        <ToggleField
          label="Default?"
          value={values.default}
          onToggle={(v) => set("default", v)}
          resetKey={`default-${initialValues.default}`}
          helpText="When true, projects will be assigned this payment term initially"
        />
      </div>

      {/* Row 3: T&E in Revenue, T&E in Cost, Include Product in Pricing */}
      <div className="row" style={{ marginTop: "1em" }}>
        <ToggleField
          label="Include T&E in Revenue"
          value={values.includeExpenseInRevenue}
          onToggle={(v) => set("includeExpenseInRevenue", v)}
          resetKey={`rev-${initialValues.includeExpenseInRevenue}`}
        />
        <ToggleField
          label="Include T&E in Cost"
          value={values.includeExpenseInCost}
          onToggle={(v) => set("includeExpenseInCost", v)}
          resetKey={`cost-${initialValues.includeExpenseInCost}`}
        />
        <ToggleField
          label="Include Product in Pricing"
          value={values.includeMaterials}
          onToggle={(v) => set("includeMaterials", v)}
          resetKey={`materials-${initialValues.includeMaterials}`}
        />
      </div>

      <div style={{ marginTop: "2em" }}>
        <TermsEditor
          termType={values.termType}
          terms={values.terms}
          onChangeTermType={(next: TermType) => set("termType", next)}
          onChangeTerms={(terms: TermFormRow[]) => set("terms", terms)}
        />
      </div>
    </Form>
  );
};

export default PaymentTermForm;
